import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Row, Col, Table, Card, Badge, Modal, Button, Form, Alert, Pagination } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHistory, faEnvelope, faUsers, faFilter, faEye, faCalendar,
  faChartLine, faCheckCircle, faTimesCircle, faClock, faSearch
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-hot-toast";

// My components
import HeaderCard from "../../components/common/HeaderCard";
import BackButton from "../../components/common/BackButton";
import Loader from "../../components/common/Loader";
import DateFormatter, { formatDate } from "../../components/common/DateFormatter";
import SecCard from "../../components/common/SecCard";

// My css
import css from "../../css/admin/home-page.module.css";

import { SERVER_ORIGIN } from "../../utilities/constants";
import { hasReadOnlyAccess, hasAdminAccess } from "../../utilities/helper_functions";

const EmailHistoryPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [emailHistory, setEmailHistory] = useState([]);
  const [stats, setStats] = useState({});
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    hasNext: false,
    hasPrev: false
  });

  // Filters
  const [filters, setFilters] = useState({
    page: 1,
    limit: 10,
    senderRole: '',
    status: '',
    startDate: '',
    endDate: ''
  });
  const [recipientPage, setRecipientPage] = useState(1);
  const [paginatedRecipients, setPaginatedRecipients] = useState([]);
  const [recipientLimit, setRecipientLimit] = useState(10); // number of rows per page
  const [recipientSearch, setRecipientSearch] = useState('');
  const [filteredRecipients, setFilteredRecipients] = useState([]);


  // const paginateRecipients = () => {
  //     if (!selectedEmail?.recipients) return;

  //     const startIndex = (recipientPage - 1) * recipientLimit;
  //     const endIndex = startIndex + recipientLimit;
  //     const paginated = selectedEmail.recipients.slice(startIndex, endIndex);
  //     setPaginatedRecipients(paginated);
  // };
  // useEffect(() => {
  //     paginateRecipients();
  // }, [selectedEmail, recipientPage, recipientLimit]);
  useEffect(() => {
    if (!selectedEmail?.recipients) return;

    const searchTerm = recipientSearch.toLowerCase();

    // Filter based on search input
    const filtered = selectedEmail.recipients.filter((recipient) =>
      [recipient.email, recipient.name, recipient.region, recipient.city, recipient.userType]
        .some((field) => field?.toLowerCase().includes(searchTerm))
    );

    setFilteredRecipients(filtered);

    const startIndex = (recipientPage - 1) * recipientLimit;
    const endIndex = startIndex + recipientLimit;
    const paginated = filtered.slice(startIndex, endIndex);
    setPaginatedRecipients(paginated);
  }, [selectedEmail, recipientSearch, recipientPage, recipientLimit]);
  useEffect(() => {
    setRecipientPage(1);
  }, [recipientSearch]);


  const navigate = useNavigate();
  const userType = sessionStorage.getItem('userType');
  const isReadOnly = hasReadOnlyAccess();

  useEffect(() => {
    fetchEmailHistory();
    fetchEmailStats();
  }, [filters]);

  const fetchEmailHistory = async () => {
    setIsLoading(true);
    try {
      const headers = {
        "Content-Type": "application/json",
        "auth-token": sessionStorage.getItem("token"),
      };

      // Add basic auth for bulk-email API
      const adminId = "yuva@gmail.com";
      const adminPassword = "admin@yuva123";
      const basicAuth = btoa(`${adminId}:${adminPassword}`);
      headers.Authorization = `Basic ${basicAuth}`;

      // Build query string
      const queryParams = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key]) {
          queryParams.append(key, filters[key]);
        }
      });

      console.log('📡 Fetching email history with filters:', {
        filters,
        queryString: queryParams.toString(),
        fullURL: `${SERVER_ORIGIN}/api/bulk-email/history?${queryParams}`
      });

      const response = await fetch(`${SERVER_ORIGIN}/api/bulk-email/history?${queryParams}`, {
        method: "GET",
        headers: headers
      });

      const result = await response.json();

      console.log('📥 Email history API response:', {
        status: response.status,
        ok: response.ok,
        dataReceived: result.data?.history?.length || 0,
        pagination: result.data?.pagination
      });

      if (response.ok) {
        setEmailHistory(result.data?.history || []);
        setPagination(result.data?.pagination || {
          currentPage: 1,
          totalPages: 1,
          totalCount: 0,
          hasNext: false,
          hasPrev: false
        });
      } else {
        toast.error(result.statusText || "Failed to fetch email history");
      }
    } catch (error) {
      console.error("Error fetching email history:", error);
      toast.error("Failed to fetch email history");
    }
    setIsLoading(false);
  };

  const fetchEmailStats = async () => {
    try {
      const headers = {
        "Content-Type": "application/json",
        "auth-token": sessionStorage.getItem("token"),
      };

      // Add basic auth for bulk-email API
      const adminId = "yuva@gmail.com";
      const adminPassword = "admin@yuva123";
      const basicAuth = btoa(`${adminId}:${adminPassword}`);
      headers.Authorization = `Basic ${basicAuth}`;

      const response = await fetch(`${SERVER_ORIGIN}/api/bulk-email/history/stats`, {
        method: "GET",
        headers: headers
      });

      const result = await response.json();

      if (response.ok) {
        setStats(result.data || {});
      }
    } catch (error) {
      console.error("Error fetching email statistics:", error);
    }
  };

  const fetchEmailDetails = async (historyId) => {
    try {
      const headers = {
        "Content-Type": "application/json",
        "auth-token": sessionStorage.getItem("token"),
      };

      // Add basic auth for bulk-email API
      const adminId = "yuva@gmail.com";
      const adminPassword = "admin@yuva123";
      const basicAuth = btoa(`${adminId}:${adminPassword}`);
      headers.Authorization = `Basic ${basicAuth}`;

      const response = await fetch(`${SERVER_ORIGIN}/api/bulk-email/history/${historyId}`, {
        method: "GET",
        headers: headers
      });

      const result = await response.json();

      if (response.ok) {
        setSelectedEmail(result.data);
        setShowDetailModal(true);
      } else {
        toast.error(result.statusText || "Failed to fetch email details");
      }
    } catch (error) {
      console.error("Error fetching email details:", error);
      toast.error("Failed to fetch email details");
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page when filters change
    }));
  };

  const handlePageChange = (page) => {
    setFilters(prev => ({ ...prev, page }));
  };
  const stripHtml = (html) => {
    if (!html) return "";
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };



  const getStatusBadge = (status) => {
    const statusConfig = {
      'sent': { variant: 'success', icon: faCheckCircle, text: 'Sent' },
      'failed': { variant: 'danger', icon: faTimesCircle, text: 'Failed' },
      'pending': { variant: 'warning', icon: faClock, text: 'Pending' },
      'partially_sent': { variant: 'warning', icon: faCheckCircle, text: 'Partially Sent' }
    };

    const config = statusConfig[status] || statusConfig['pending'];

    return (
      <Badge bg={config.variant}>
        <FontAwesomeIcon icon={config.icon} className="me-1" />
        {config.text}
      </Badge>
    );
  };

  const getSenderRoleBadge = (role) => {
    const roleConfig = {
      Admin: { variant: "primary", text: "Admin" },
      "Regional Mentor": { variant: "info", text: "Regional Mentor" },
      "Chapter EM": { variant: "secondary", text: "Chapter EM" },
    };

    const config = roleConfig[role] || { variant: 'light', text: role };

    return <Badge bg={config.variant}>{config.text}</Badge>;
  };

  const getDashboardPath = () => {
    if (userType === 'admin' || userType === 'regionem') {
      return '/admin/services';
    } else if (userType === 'em') {
      return '/em/services';
    }
    return '/admin/services';
  };

  if (isLoading && emailHistory.length === 0) {
    return <Loader />;
  }

  return (
    <div className={css.outerDiv}>
      {/* <HeaderCard>
                <BackButton text="Back to Dashboard" customPath={getDashboardPath()} />
                <h1 className="headerTitle">
                    <FontAwesomeIcon icon={faHistory} style={{ marginRight: "10px" }} />
                    Email History
                </h1>
                <hr />
                <p className="headerSubtitle">
                    View and manage all bulk emails sent through the system. Track delivery status,
                    recipients, and email performance.
                </p>
            </HeaderCard> */}

      <div className={css.headerSection}>
        <div className={css.headerLeft}>
          <BackButton
            text="Back to Dashboard"
            customPath={getDashboardPath()}
          />
        </div>
      </div>
      {hasReadOnlyAccess() && (
        <div className={css.readOnlyAlert}>
          <div className="alert alert-info" role="alert">
            <strong>Read-Only Access:</strong> You can view all Email History
            data but cannot add, edit, or delete any records.
          </div>
        </div>
      )}
      <div className="my-4">
        <SecCard>
          <Row className="g-4">
            <div
              className={`${css.serviceHeader} d-flex justify-content-between align-items-center mb-3`}
            >
              <div className={css.serviceName}>
                {" "}
                <FontAwesomeIcon
                  icon={faHistory}
                  style={{ marginRight: "10px" }}
                />
                Email History
              </div>

              {/* <div className={css.headerRight}>
                  {!hasReadOnlyAccess() && (
                    <button
                      className={css.addBtn}
                      onClick={handleAddTermsClick}
                    >
                      <FontAwesomeIcon icon={faPlus} className={css.addIcon} />
                      Add Terms and Conditions{" "}
                    </button>
                  )}
                </div> */}
            </div>
          </Row>
        </SecCard>
      </div>

      {/* Statistics Cards */}
      <div className="container-fluid mt-4">
        <Row className="mb-4">
          <Col lg={3} md={6} className="mb-3">
            <Card className="border-left-primary shadow h-100">
              <Card.Body>
                <Row>
                  <Col>
                    <div className="text-xs font-weight-bold text-primary text-uppercase mb-1">
                      Total Emails
                    </div>
                    <div className="h5 mb-0 font-weight-bold text-gray-800">
                      {stats.totalEmails || 0}
                    </div>
                  </Col>
                  <Col xs="auto">
                    <FontAwesomeIcon
                      icon={faEnvelope}
                      className="fa-2x text-gray-300"
                    />
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={3} md={6} className="mb-3">
            <Card className="border-left-success shadow h-100">
              <Card.Body>
                <Row>
                  <Col>
                    <div className="text-xs font-weight-bold text-success text-uppercase mb-1">
                      Successful Emails
                    </div>
                    <div className="h5 mb-0 font-weight-bold text-gray-800">
                      {stats.successfulEmails || 0}
                    </div>
                    <small className="text-muted">
                      Success Rate: {stats.successRate || 0}%
                    </small>
                  </Col>
                  <Col xs="auto">
                    <FontAwesomeIcon
                      icon={faCheckCircle}
                      className="fa-2x text-gray-300"
                    />
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={3} md={6} className="mb-3">
            <Card className="border-left-info shadow h-100">
              <Card.Body>
                <Row>
                  <Col>
                    <div className="text-xs font-weight-bold text-info text-uppercase mb-1">
                      Total Recipients
                    </div>
                    <div className="h5 mb-0 font-weight-bold text-gray-800">
                      {stats.totalRecipients || 0}
                    </div>
                  </Col>
                  <Col xs="auto">
                    <FontAwesomeIcon
                      icon={faUsers}
                      className="fa-2x text-gray-300"
                    />
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={3} md={6} className="mb-3">
            <Card className="border-left-danger shadow h-100">
              <Card.Body>
                <Row>
                  <Col>
                    <div className="text-xs font-weight-bold text-danger text-uppercase mb-1">
                      Failed Emails
                    </div>
                    <div className="h5 mb-0 font-weight-bold text-gray-800">
                      {stats.failedEmails || 0}
                    </div>
                    <small className="text-muted">
                      Last 30 days: {stats.emailsLast30Days || 0}
                    </small>
                  </Col>
                  <Col xs="auto">
                    <FontAwesomeIcon
                      icon={faTimesCircle}
                      className="fa-2x text-gray-300"
                    />
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Filters */}
        <Card className="mb-4">
          <Card.Header>
            <h5>
              <FontAwesomeIcon icon={faFilter} className="me-2" />
              Filters
            </h5>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Sender Role</Form.Label>
                  <Form.Select
                    value={filters.senderRole}
                    onChange={(e) =>
                      handleFilterChange("senderRole", e.target.value)
                    }
                  >
                    <option value="">All Roles</option>
                    <option value="Admin">Admin</option>
                    <option value="Regional Mentor">Regional Mentor</option>
                    <option value="Chapter EM">Chapter EM</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={filters.status}
                    onChange={(e) =>
                      handleFilterChange("status", e.target.value)
                    }
                  >
                    <option value="">All Status</option>
                    <option value="sent">Sent</option>
                    <option value="failed">Failed</option>
                    <option value="pending">Pending</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Start Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={filters.startDate}
                    onChange={(e) =>
                      handleFilterChange("startDate", e.target.value)
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>End Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={filters.endDate}
                    onChange={(e) =>
                      handleFilterChange("endDate", e.target.value)
                    }
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Email History Table */}
        <Card>
          <Card.Header className="d-flex justify-content-between align-items-center">
            <h5>
              <FontAwesomeIcon icon={faEnvelope} className="me-2" />
              Email History
            </h5>
            <Badge bg="secondary">
              {pagination.totalCount || 0} total emails
            </Badge>
          </Card.Header>
          <Card.Body>
            {emailHistory.length === 0 ? (
              <Alert variant="info" className="text-center">
                <FontAwesomeIcon
                  icon={faEnvelope}
                  size="2x"
                  className="mb-3"
                />
                <h5>No emails found</h5>
                <p>
                  No emails match your current filters. Try adjusting the
                  filter criteria.
                </p>
              </Alert>
            ) : (
              <>
                <Table responsive striped hover>
                  <thead>
                    <tr>
                      <th>Sent Date</th>
                      <th>Subject</th>
                      <th>Sender</th>
                      <th>Recipients</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {emailHistory.map((email) => (
                      <tr key={email._id}>
                        <td>
                          <DateFormatter
                            date={email.sentAt}
                            format="short"
                            showTime={true}
                            className="text-muted small"
                          />
                        </td>
                        <td>
                          <div style={{ maxWidth: "200px" }}>
                            <div className="fw-bold">{email.subject}</div>
                            {/* <small className="text-muted">
                              {email.message
                                ? email.message.substring(0, 50) + "..."
                                : "No preview available"}
                            </small> */}
                            <small className="text-muted">
                              {email.message
                                ? stripHtml(email.message).substring(0, 50) + "..."
                                : "No preview available"}
                            </small>

                          </div>
                        </td>
                        <td>
                          <div>
                            <div className="fw-bold">{email.senderName}</div>
                            {getSenderRoleBadge(email.senderRole)}
                          </div>
                        </td>
                        <td>
                          <div>
                            <Badge bg="info" className="me-1">
                              {email.recipientCount} total
                            </Badge>
                            <br />
                            <small>
                              <Badge bg="success" className="me-1">
                                {email.stats?.totalSent || 0} sent
                              </Badge>
                              <Badge bg="danger">
                                {email.stats?.totalFailed || 0} failed
                              </Badge>
                            </small>
                          </div>
                        </td>
                        <td>{getStatusBadge(email.status)}</td>
                        <td>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => fetchEmailDetails(email._id)}
                          >
                            <FontAwesomeIcon icon={faEye} className="me-1" />
                            View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>

                {/* Pagination */}
                {(pagination?.totalPages || 0) > 1 && (
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <div>
                      Showing{" "}
                      {((pagination?.currentPage || 1) - 1) * filters.limit +
                        1}{" "}
                      to{" "}
                      {Math.min(
                        (pagination?.currentPage || 1) * filters.limit,
                        pagination?.totalCount || 0
                      )}{" "}
                      of {pagination?.totalCount || 0} emails
                    </div>
                    <Pagination>
                      <Pagination.Prev
                        disabled={!pagination?.hasPrev}
                        onClick={() =>
                          handlePageChange((pagination?.currentPage || 1) - 1)
                        }
                      />
                      {[
                        ...Array(Math.min(pagination?.totalPages || 1, 5)),
                      ].map((_, index) => {
                        const page =
                          (pagination?.currentPage || 1) - 2 + index;
                        if (
                          page > 0 &&
                          page <= (pagination?.totalPages || 1)
                        ) {
                          return (
                            <Pagination.Item
                              key={page}
                              active={page === (pagination?.currentPage || 1)}
                              onClick={() => handlePageChange(page)}
                            >
                              {page}
                            </Pagination.Item>
                          );
                        }
                        return null;
                      })}
                      <Pagination.Next
                        disabled={!pagination?.hasNext}
                        onClick={() =>
                          handlePageChange((pagination?.currentPage || 1) + 1)
                        }
                      />
                    </Pagination>
                  </div>
                )}
              </>
            )}
          </Card.Body>
        </Card>
      </div>

      {/* Email Detail Modal */}
      <Modal
        show={showDetailModal}
        onHide={() => setShowDetailModal(false)}
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faEnvelope} className="me-2" />
            Email Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedEmail && (
            <div>
              <Row className="mb-3">
                <Col md={6}>
                  <h6>Email Information</h6>
                  <p>
                    <strong>Subject:</strong> {selectedEmail.subject}
                  </p>
                  <p>
                    <strong>Sent Date:</strong>
                    <DateFormatter
                      date={selectedEmail.sentAt}
                      format="full"
                      showTime={true}
                      className="ms-2"
                    />
                  </p>
                  <p>
                    <strong>Status:</strong>{" "}
                    {getStatusBadge(selectedEmail.status)}
                  </p>
                </Col>
                <Col md={6}>
                  <h6>Sender Information</h6>
                  <p>
                    <strong>Name:</strong> {selectedEmail.senderName}
                  </p>
                  <p>
                    <strong>Role:</strong>{" "}
                    {getSenderRoleBadge(selectedEmail.senderRole)}
                  </p>
                  <p>
                    <strong>Total Recipients:</strong>{" "}
                    <Badge bg="info">{selectedEmail.recipientCount}</Badge>
                  </p>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col>
                  <h6>Delivery Statistics</h6>
                  <Row>
                    <Col md={4}>
                      <div className="text-center p-3 border rounded">
                        <div className="h3 text-success mb-2">
                          <FontAwesomeIcon icon={faCheckCircle} />
                        </div>
                        <div className="h4 text-success">
                          {selectedEmail.stats?.totalSent || 0}
                        </div>
                        <small className="text-muted">
                          Successfully Sent
                        </small>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="text-center p-3 border rounded">
                        <div className="h3 text-danger mb-2">
                          <FontAwesomeIcon icon={faTimesCircle} />
                        </div>
                        <div className="h4 text-danger">
                          {selectedEmail.stats?.totalFailed || 0}
                        </div>
                        <small className="text-muted">Failed to Send</small>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="text-center p-3 border rounded">
                        <div className="h3 text-info mb-2">
                          <FontAwesomeIcon icon={faChartLine} />
                        </div>
                        <div className="h4 text-info">
                          {selectedEmail.recipientCount > 0
                            ? Math.round(
                              ((selectedEmail.stats?.totalSent || 0) /
                                selectedEmail.recipientCount) *
                              100
                            )
                            : 0}
                          %
                        </div>
                        <small className="text-muted">Success Rate</small>
                      </div>
                    </Col>
                  </Row>
                </Col>
              </Row>

              <hr />
              <Form.Group className="mb-3" controlId="recipientSearch">
                <Form.Control
                  type="text"
                  placeholder="Search by email, name, region, city, or user type"
                  value={recipientSearch}
                  onChange={(e) => setRecipientSearch(e.target.value)}
                />
              </Form.Group>

              <Row className="mb-3">
                <Col>
                  <h6>Recipient List</h6>
                  <Table striped bordered hover responsive>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Email</th>
                        <th>Name</th>
                        <th>Delivery Status</th>
                        <th>Delivered At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedRecipients.length > 0 ? (
                        paginatedRecipients.map((recipient, idx) => (
                          <tr key={recipient.email}>
                            <td>
                              {(recipientPage - 1) * recipientLimit + idx + 1}
                            </td>
                            <td>{recipient.email}</td>
                            <td>{recipient.name}</td>

                            <td>{recipient.deliveryStatus}</td>
                            <td>
                              {recipient.deliveryTime
                                ? formatDate(recipient.deliveryTime, "short")
                                : "Not Delivered"}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6" className="text-center text-muted">
                            No recipient data available.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>

                  {/* Pagination Controls */}
                  {/* {selectedEmail?.recipients?.length > recipientLimit && (
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div>
                                                Showing {(recipientPage - 1) * recipientLimit + 1} to{" "}
                                                {Math.min(recipientPage * recipientLimit, selectedEmail.recipients.length)} of{" "}
                                                {selectedEmail.recipients.length}
                                            </div>
                                            <Pagination className="mb-0">
                                                <Pagination.Prev
                                                    disabled={recipientPage === 1}
                                                    onClick={() => setRecipientPage((prev) => Math.max(prev - 1, 1))}
                                                />
                                                {[...Array(Math.ceil(selectedEmail.recipients.length / recipientLimit))].map((_, index) => (
                                                    <Pagination.Item
                                                        key={index + 1}
                                                        active={index + 1 === recipientPage}
                                                        onClick={() => setRecipientPage(index + 1)}
                                                    >
                                                        {index + 1}
                                                    </Pagination.Item>
                                                ))}
                                                <Pagination.Next
                                                    disabled={recipientPage === Math.ceil(selectedEmail.recipients.length / recipientLimit)}
                                                    onClick={() =>
                                                        setRecipientPage((prev) =>
                                                            Math.min(prev + 1, Math.ceil(selectedEmail.recipients.length / recipientLimit))
                                                        )
                                                    }
                                                />
                                            </Pagination>
                                        </div>
                                    )} */}
                  {/* Pagination Controls */}
                  {selectedEmail?.recipients?.length > recipientLimit && (
                    <div className="d-flex justify-content-between align-items-center">
                      {/* Range text */}
                      <div>
                        Showing {(recipientPage - 1) * recipientLimit + 1}–
                        {Math.min(
                          recipientPage * recipientLimit,
                          selectedEmail.recipients.length
                        )}{" "}
                        of {selectedEmail.recipients.length}
                      </div>

                      {/* Prev / Next */}
                      <Pagination className="mb-0">
                        <Pagination.Prev
                          disabled={recipientPage === 1}
                          onClick={() =>
                            setRecipientPage((prev) => Math.max(prev - 1, 1))
                          }
                        />
                        <Pagination.Next
                          disabled={
                            recipientPage ===
                            Math.ceil(
                              selectedEmail.recipients.length / recipientLimit
                            )
                          }
                          onClick={() =>
                            setRecipientPage((prev) =>
                              Math.min(
                                prev + 1,
                                Math.ceil(
                                  selectedEmail.recipients.length /
                                  recipientLimit
                                )
                              )
                            )
                          }
                        />
                      </Pagination>
                    </div>
                  )}
                </Col>
              </Row>

              <hr />

              <Row className="mb-3">
                {/* <Col>
                  <h6>Message Content</h6>
                  <div
                    className="border p-3 bg-light"
                    style={{ maxHeight: "200px", overflowY: "auto" }}
                  >
                    {selectedEmail.message}
                  </div>
                </Col> */}
                <Col>
                  <h6>Message Content</h6>
                  <div
                    className="border p-3 bg-light"
                    style={{ maxHeight: "200px", overflowY: "auto", whiteSpace: "pre-wrap" }}
                  >
                    {stripHtml(selectedEmail.message)}
                  </div>
                </Col>

              </Row>

              <Row className="mb-3">
                <Col>
                  <h6>Recipients by User Type</h6>
                  <Row>
                    <Col md={3}>
                      <div className="text-center">
                        <div className="h4 text-primary">
                          {selectedEmail.stats?.byUserType?.students || 0}
                        </div>
                        <small>Students</small>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="text-center">
                        <div className="h4 text-info">
                          {selectedEmail.stats?.byUserType?.institutes || 0}
                        </div>
                        <small>Institutes</small>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="text-center">
                        <div className="h4 text-warning">
                          {selectedEmail.stats?.byUserType?.ems || 0}
                        </div>
                        <small>Chapter EMs</small>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="text-center">
                        <div className="h4 text-secondary">
                          {selectedEmail.stats?.byUserType?.regionems || 0}
                        </div>
                        <small>Regional Mentors</small>
                      </div>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDetailModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default EmailHistoryPage; 